import BubbleMenu from '@tiptap/extension-bubble-menu';
import FloatingMenu from '@tiptap/extension-floating-menu';

import { ICONS } from './icons';
import { element, iconButton, insertImageFromFile, colorControl } from './toolbar';

const FONT_STEP = 2;
const FONT_MIN = 8;
const FONT_MAX = 72;
const FONT_DEFAULT = '14pt';
// Text glyphs (not SVG) for the font-size steppers, styled by `.tr-editor__fontstep`.
const FONT_INCREASE_GLYPH = '<span class="tr-editor__fontstep">A+</span>';
const FONT_DECREASE_GLYPH = '<span class="tr-editor__fontstep">A−</span>';

// Bumps the current font size by `delta`, reading the active textStyle size (or the
// default when none is set) and preserving its unit. Backed by the FontSize extension.
function stepFontSize(editor, delta) {
  const current = editor.getAttributes('textStyle').fontSize || FONT_DEFAULT;
  const value = parseFloat(current);
  const unit = String(current).replace(/[\d.\s]/g, '') || 'pt';
  const base = Number.isFinite(value) ? value : parseFloat(FONT_DEFAULT);
  const next = Math.min(FONT_MAX, Math.max(FONT_MIN, base + delta));
  editor.chain().focus().setFontSize(`${next}${unit}`).run();
}

// Builds the "almost headless" floating UI for the bubble editor variant: a bubble bar
// that appears over a text selection (inline formatting) and a floating bar that appears
// on an empty line (block insertion). No fixed toolbar is rendered.
//
// The Editor instance does not exist yet when the extensions are created (they must be
// in the extensions array passed to `new Editor`), so button handlers and the active-
// state highlighting resolve the editor lazily through `bindEditor`, called right after
// construction.
//
// `host` is the `.tr-editor` container; the menus are appended inside it (via tippy's
// appendTo) so they inherit the scoped `.tr-editor__btn` styling instead of being
// portaled to <body> where those rules would not reach them.
//
// Returns:
//   extensions → [BubbleMenu, FloatingMenu] to spread into the editor's extensions
//   bindEditor → (editor) => wires the click handlers, transaction refresh and cleanup
export function buildBubbleMenus({ host, onUploadImage, floatingMenu = true } = {}) {
  const updaters = [];
  let currentEditor = null;
  const run = (command) => {
    if (!currentEditor) return;
    command(currentEditor.chain().focus()).run();
  };
  // Font-size stepper button bound to the lazily-resolved editor. Shared by both bars.
  const fontButton = (glyph, title, delta) =>
    iconButton(glyph, title, () => { if (currentEditor) stepFontSize(currentEditor, delta); }, []);

  // ---- Bubble bar (on text selection): inline formatting --------------------
  const bubbleBar = element('div', 'tr-editor__bubble');
  const inlineButton = (svg, title, command, isActive) => {
    const button = iconButton(svg, title, () => run(command), updaters, isActive ? () => currentEditor && isActive() : null);
    bubbleBar.appendChild(button);
  };
  inlineButton(ICONS.bold, 'Negrito', (chain) => chain.toggleBold(), () => currentEditor.isActive('bold'));
  inlineButton(ICONS.italic, 'Itálico', (chain) => chain.toggleItalic(), () => currentEditor.isActive('italic'));
  inlineButton(ICONS.strikethrough, 'Tachado', (chain) => chain.toggleStrike(), () => currentEditor.isActive('strike'));
  inlineButton(ICONS.underline, 'Sublinhado', (chain) => chain.toggleUnderline(), () => currentEditor.isActive('underline'));
  bubbleBar.appendChild(
    iconButton(ICONS.link, 'Inserir link', () => {
      const url = window.prompt('URL do link:');
      if (url) run((chain) => chain.setLink({ href: url }));
      else run((chain) => chain.unsetLink());
    }, updaters, () => currentEditor && currentEditor.isActive('link'))
  );
  bubbleBar.appendChild(fontButton(FONT_INCREASE_GLYPH, 'Aumentar a fonte', FONT_STEP));
  bubbleBar.appendChild(fontButton(FONT_DECREASE_GLYPH, 'Reduzir fonte', -FONT_STEP));

  // ---- Floating bar (on empty line): block insertion ------------------------
  const floatingBar = element('div', 'tr-editor__floating');
  const blockButton = (svg, title, command, isActive) => {
    floatingBar.appendChild(iconButton(svg, title, () => run(command), updaters, isActive ? () => currentEditor && isActive() : null));
  };
  floatingBar.appendChild(fontButton(FONT_INCREASE_GLYPH, 'Aumentar a fonte', FONT_STEP));
  floatingBar.appendChild(fontButton(FONT_DECREASE_GLYPH, 'Reduzir fonte', -FONT_STEP));
  blockButton(ICONS.listBullets, 'Lista com marcadores', (chain) => chain.toggleBulletList(), () => currentEditor.isActive('bulletList'));
  blockButton(ICONS.listNumbers, 'Lista numerada', (chain) => chain.toggleOrderedList(), () => currentEditor.isActive('orderedList'));
  floatingBar.appendChild(
    iconButton(ICONS.image, 'Inserir imagem', () => {
      if (currentEditor) insertImageFromFile(currentEditor, onUploadImage);
    }, [])
  );

  // The floating (empty-line) menu is always registered but gated by a runtime flag so
  // the header toggle can switch it on/off live. `floatingShouldShow` mirrors Tiptap's
  // default (focused, empty selection at the top level of an empty text block) and adds
  // the `floatingEnabled` check on top.
  let floatingEnabled = floatingMenu;
  const floatingShouldShow = ({ editor, view, state }) => {
    if (!floatingEnabled) return false;
    const { $anchor, empty } = state.selection;
    const isRootDepth = $anchor.depth === 1;
    const isEmptyTextBlock = $anchor.parent.isTextblock
      && !$anchor.parent.type.spec.code
      && !$anchor.parent.textContent;
    return view.hasFocus() && empty && isRootDepth && isEmptyTextBlock && editor.isEditable;
  };

  const tippyOptions = { theme: 'light', maxWidth: 'none', appendTo: () => host || document.body };
  const extensions = [
    BubbleMenu.configure({ element: bubbleBar, tippyOptions: { ...tippyOptions, placement: 'top' } }),
    FloatingMenu.configure({ element: floatingBar, tippyOptions: { ...tippyOptions, placement: 'left' }, shouldShow: floatingShouldShow })
  ];

  // Exposed to the header so a button can toggle the floating menu at runtime.
  const floating = {
    isEnabled: () => floatingEnabled,
    toggle: () => {
      floatingEnabled = !floatingEnabled;
      if (!floatingEnabled) {
        const root = typeof floatingBar.closest === 'function' ? floatingBar.closest('[data-tippy-root]') : null;
        if (root && root._tippy && root._tippy.state && root._tippy.state.isVisible) root._tippy.hide();
      } else if (currentEditor) {
        // Empty transaction: nudges the plugin to re-run shouldShow so it can pop now.
        currentEditor.view.dispatch(currentEditor.state.tr);
      }
      return floatingEnabled;
    }
  };

  function bindEditor(editor) {
    currentEditor = editor;
    const refresh = () => updaters.forEach((update) => update());
    editor.on('transaction', refresh);
    refresh();
  }

  // ProseMirror keydown hook: Escape closes whichever floating bar is currently open.
  // Only reports the key as handled when a bar was actually visible, so Escape still
  // propagates (e.g. to close a modal/fullscreen) when no menu is open.
  function handleKeyDown(view, event) {
    if (event.key !== 'Escape') return false;
    let handled = false;
    for (const bar of [bubbleBar, floatingBar]) {
      const root = typeof bar.closest === 'function' ? bar.closest('[data-tippy-root]') : null;
      const instance = root && root._tippy;
      if (instance && instance.state && instance.state.isVisible) {
        instance.hide();
        handled = true;
      }
    }
    return handled;
  }

  return { extensions, bindEditor, handleKeyDown, floating };
}

// Compact fixed header for the bubble variant: a slim toolbar with the essential
// controls (text color, font size, bold, italic, link, image, horizontal rule) plus a
// toggle for the empty-line floating menu. Built after the editor exists, so the live
// instance is used directly. The bubble/floating menus stay available for
// selection/empty-line formatting; this bar just surfaces the basics.
export function buildBubbleHeader(editor, { onUploadImage, floating } = {}) {
  const header = element('div', 'tr-editor__toolbar');
  const updaters = [];

  header.appendChild(colorControl(editor));
  header.appendChild(iconButton(FONT_INCREASE_GLYPH, 'Aumentar a fonte', () => stepFontSize(editor, FONT_STEP), []));
  header.appendChild(iconButton(FONT_DECREASE_GLYPH, 'Reduzir fonte', () => stepFontSize(editor, -FONT_STEP), []));
  header.appendChild(iconButton(ICONS.bold, 'Negrito', () => editor.chain().focus().toggleBold().run(), updaters, () => editor.isActive('bold')));
  header.appendChild(iconButton(ICONS.italic, 'Itálico', () => editor.chain().focus().toggleItalic().run(), updaters, () => editor.isActive('italic')));
  header.appendChild(
    iconButton(ICONS.link, 'Inserir link', () => {
      const url = window.prompt('URL do link:');
      if (url) editor.chain().focus().setLink({ href: url }).run();
      else editor.chain().focus().unsetLink().run();
    }, updaters, () => editor.isActive('link'))
  );
  header.appendChild(iconButton(ICONS.image, 'Inserir imagem', () => insertImageFromFile(editor, onUploadImage), []));
  header.appendChild(iconButton(ICONS.horizontalRule, 'Linha horizontal', () => editor.chain().focus().setHorizontalRule().run(), []));

  // Toggle for the empty-line floating menu. `is-active` reflects the current state.
  if (floating) {
    const floatingToggle = iconButton(ICONS.floatingMenu, 'Alternar menu de linha vazia', () => {
      floating.toggle();
      floatingToggle.classList.toggle('is-active', floating.isEnabled());
    }, []);
    floatingToggle.classList.toggle('is-active', floating.isEnabled());
    header.appendChild(floatingToggle);
  }

  const refresh = () => updaters.forEach((update) => update());
  editor.on('transaction', refresh);
  refresh();
  return header;
}
